import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  type CSSProperties,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react'
import { useNavigate } from 'react-router-dom'
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  XMarkIcon,
  ClipboardDocumentIcon,
  MagnifyingGlassIcon,
  ChevronDownIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline'
import { useEmailSearchStore } from '@/store/emailSearchStore'
import { useEmailRulesFiltersStore } from '@/store/emailRulesFiltersStore'
import { toast } from '@/store/toastStore'
import {
  getEmailRules,
  createEmailRule,
  updateEmailRule,
  deleteEmailRule,
} from '@/services/emailRules'
import type { EmailRule, EmailRuleCreate, EmailRuleUpdate } from '@/types/emailRule'
import EmailRuleForm from '@/components/EmailRuleForm'
import { useMaxMd } from '@/hooks/useMaxMd'
import {
  ALL_COLUMN_KEYS,
  EMAIL_RULES_COLUMN_LABELS,
  EMAIL_RULES_DEFAULT_SORT,
  EMAIL_RULES_DEFAULT_WIDTHS,
  EMAIL_RULES_FIELD_DEFINITIONS,
  EMAIL_RULES_PAGE_KEY,
  buildEmailRulesFilterFields,
  fixedColumnStyle,
  isEmailRulesColumnSortable,
  migrateLegacyColumnWidths,
  type EmailRulesColumnKey,
} from '@/pages/emailRulesTableConfig'
import {
  ActiveFilterPillBar,
  ColumnPicker,
  ColumnResizeHandle,
  FilterBuilderDrawer,
  SearchInput,
  SortBuilder,
  TablePageShell,
  TableToolbar,
  applySorts,
  countFilterConditions,
  generateFilterPills,
  getDefaultColumnVisibility,
  getVisibleFieldKeys,
  loadColumnVisibility,
  removeFilterPill,
  saveColumnVisibility,
  useColumnWidths,
  type ColumnVisibility,
  type SortDirection,
  type SortSpec,
} from '@/tableControls'
import {
  filterEmailRulesByText,
  filterEmailRulesWithTree,
} from '@/utils/emailRulesResultFilters'

/** Match services/gmail_service.py _gmail_format_from_to_operand for copy/paste into Gmail. */
function gmailFromToOperand(raw: string): string {
  const v = raw.replace(/^@+/, '').trim()
  if (!v) return v
  if (v.length >= 2 && v.startsWith('"') && v.endsWith('"')) return v
  if (v.includes(' ')) {
    const inner = v.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
    return `"${inner}"`
  }
  return v
}

/** Match services/gmail_service.py _gmail_format_subject_clause. */
function gmailSubjectClause(subject: string): string {
  const subj = subject.trim()
  if (!subj) return ''
  if (subj.startsWith('(') && subj.endsWith(')')) return `subject:${subj}`
  return `subject:(${subj})`
}

function EmailRulesSortableHeader({
  label,
  sortKey,
  sortDirection,
  onSort,
  className = '',
  title,
  columnKey,
  style,
  onStartResize,
  showResize = true,
}: {
  label: ReactNode
  sortKey: string
  sortDirection: SortDirection | null
  onSort: (key: string) => void
  className?: string
  title?: string
  columnKey: string
  style?: CSSProperties
  onStartResize?: (key: string, event: ReactMouseEvent) => void
  showResize?: boolean
}) {
  const isSorted = sortDirection !== null

  return (
    <th
      className={`sortable relative ${isSorted ? 'sorted' : ''} ${className}`}
      style={style}
      title={title}
      onClick={() => onSort(sortKey)}
    >
      <span className="inline-flex items-center gap-1 pr-2">
        {label}
        <span className="sort-indicator">
          {sortDirection === 'asc' ? (
            <ChevronUpIcon className="h-3 w-3" />
          ) : sortDirection === 'desc' ? (
            <ChevronDownIcon className="h-3 w-3" />
          ) : (
            <ChevronUpDownIcon className="h-3 w-3" />
          )}
        </span>
      </span>
      {showResize && onStartResize ? (
        <ColumnResizeHandle columnKey={columnKey} onStartResize={onStartResize} />
      ) : null}
    </th>
  )
}

export default function EmailRules() {
  const navigate = useNavigate()
  const setSearchMode = useEmailSearchStore((s) => s.setSearchMode)
  const setSelectedRuleIds = useEmailSearchStore((s) => s.setSelectedRuleIds)

  const [rules, setRules] = useState<EmailRule[]>([])
  const [loading, setLoading] = useState(true)
  const [editingRule, setEditingRule] = useState<EmailRule | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const filterTree = useEmailRulesFiltersStore((s) => s.filterTree)
  const textSearch = useEmailRulesFiltersStore((s) => s.textSearch)
  const setFilterTree = useEmailRulesFiltersStore((s) => s.setFilterTree)
  const setTextSearch = useEmailRulesFiltersStore((s) => s.setTextSearch)
  const clearFilters = useEmailRulesFiltersStore((s) => s.clearFilters)

  const [showFilters, setShowFilters] = useState(false)
  const [showSort, setShowSort] = useState(false)
  const [showColumns, setShowColumns] = useState(false)
  const [sortSpecs, setSortSpecs] = useState<SortSpec[]>(EMAIL_RULES_DEFAULT_SORT)
  const [columnVisibility, setColumnVisibility] = useState<ColumnVisibility>(() =>
    loadColumnVisibility(EMAIL_RULES_PAGE_KEY, EMAIL_RULES_FIELD_DEFINITIONS)
  )

  useEffect(() => {
    migrateLegacyColumnWidths()
  }, [])

  useEffect(() => {
    useEmailRulesFiltersStore.getState().expireSessionIfStale()
  }, [])

  useEffect(() => {
    const id = window.setInterval(() => {
      useEmailRulesFiltersStore.getState().expireSessionIfStale()
    }, 60_000)
    return () => window.clearInterval(id)
  }, [])

  useEffect(() => {
    loadRules()
  }, [])

  async function loadRules() {
    try {
      setLoading(true)
      const data = await getEmailRules()
      setRules(data.rules)
    } catch (err) {
      toast.error('Failed to load email rules')
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function handleCreate(data: EmailRuleCreate) {
    try {
      const newRule = await createEmailRule(data)
      setRules((prev) => [...prev, newRule])
      setIsCreating(false)
    } catch (err) {
      toast.error('Failed to create rule')
      console.error(err)
    }
  }

  async function handleUpdate(id: number, data: EmailRuleUpdate) {
    try {
      const updated = await updateEmailRule(id, data)
      setRules((prev) => prev.map((r) => (r.id === id ? updated : r)))
      setEditingRule(null)
    } catch (err) {
      toast.error('Failed to update rule')
      console.error(err)
    }
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this rule?')) return

    try {
      await deleteEmailRule(id)
      setRules((prev) => prev.filter((r) => r.id !== id))
    } catch (err) {
      toast.error('Failed to delete rule')
      console.error(err)
    }
  }

  async function toggleStatus(rule: EmailRule) {
    const newStatus = rule.status === 'confirmed' ? 'disabled' : 'confirmed'
    await handleUpdate(rule.id, { status: newStatus })
  }

  function buildGmailQuery(rule: EmailRule): string {
    const parts: string[] = []

    if (rule.from_filter) {
      const op = gmailFromToOperand(rule.from_filter)
      if (op) parts.push(`from:${op}`)
    }

    if (rule.subject_contains) {
      const sc = gmailSubjectClause(rule.subject_contains)
      if (sc) parts.push(sc)
    }

    if (rule.email_has_words) {
      const words = rule.email_has_words.split(',').map((w) => w.trim()).filter((w) => w)
      if (words.length > 0) {
        parts.push(`{${words.join(' ')}}`)
      }
    }

    if (rule.has_attachment) {
      parts.push('has:attachment')
    }

    return parts.join(' ')
  }

  async function copyGmailQuery(rule: EmailRule) {
    const query = buildGmailQuery(rule)
    try {
      await navigator.clipboard.writeText(query)
    } catch (err) {
      console.error('Failed to copy to clipboard:', err)
      toast.error('Failed to copy to clipboard')
    }
  }

  function openEmailSearchForRule(ruleId: number) {
    setSearchMode('rules')
    setSelectedRuleIds([ruleId])
    navigate('/bookkeeper/email-search')
  }

  const uniqueDestinations = useMemo(() => {
    const dests = new Set<string>()
    rules.forEach((r) => r.destinations.forEach((d) => dests.add(d)))
    return Array.from(dests).sort()
  }, [rules])

  const filterFields = useMemo(
    () =>
      buildEmailRulesFilterFields(
        uniqueDestinations.map((dest) => ({
          value: dest,
          label: dest.toUpperCase(),
        }))
      ),
    [uniqueDestinations]
  )

  const treeFilteredRules = useMemo(
    () => filterEmailRulesWithTree(rules, filterTree, filterFields),
    [rules, filterTree, filterFields]
  )

  const filteredRules = useMemo(
    () => filterEmailRulesByText(treeFilteredRules, textSearch),
    [treeFilteredRules, textSearch]
  )

  const sortedRules = useMemo(
    () => applySorts(filteredRules, sortSpecs, EMAIL_RULES_FIELD_DEFINITIONS),
    [filteredRules, sortSpecs]
  )

  const getSortDirection = useCallback(
    (field: string): SortDirection | null => {
      const spec = sortSpecs.find((entry) => entry.field === field)
      return spec?.direction ?? null
    },
    [sortSpecs]
  )

  const handleRequestSort = useCallback((field: string) => {
    setSortSpecs((prev) => {
      const existing = prev.find((entry) => entry.field === field)
      if (existing) {
        return [{ field, direction: existing.direction === 'asc' ? 'desc' : 'asc' }]
      }
      return [{ field, direction: 'asc' }]
    })
  }, [])

  const visibleColumnKeys = useMemo(
    () => getVisibleFieldKeys(EMAIL_RULES_FIELD_DEFINITIONS, columnVisibility),
    [columnVisibility]
  )

  const visibleColumnCount = visibleColumnKeys.length

  const handleColumnVisibilityChange = useCallback((visibility: ColumnVisibility) => {
    setColumnVisibility(visibility)
    saveColumnVisibility(EMAIL_RULES_PAGE_KEY, visibility)
  }, [])

  const { getWidth, startResize } = useColumnWidths({
    pageKey: EMAIL_RULES_PAGE_KEY,
    columnKeys: [...ALL_COLUMN_KEYS],
    defaults: EMAIL_RULES_DEFAULT_WIDTHS,
  })
  const maxMd = useMaxMd()
  const allowColumnResize = !maxMd

  const visibleTableWidth = useMemo(
    () => visibleColumnKeys.reduce((sum, key) => sum + getWidth(key), 0),
    [getWidth, visibleColumnKeys]
  )

  const tableStyle = useMemo((): CSSProperties | undefined => {
    if (!allowColumnResize) return undefined
    return {
      width: visibleTableWidth,
      minWidth: visibleTableWidth,
      tableLayout: 'fixed',
    }
  }, [allowColumnResize, visibleTableWidth])

  const headerStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return {
        ...fixedColumnStyle(getWidth(columnKey)),
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }
    },
    [allowColumnResize, getWidth]
  )

  const cellStyle = useCallback(
    (columnKey: string): CSSProperties | undefined => {
      if (!allowColumnResize) return undefined
      return fixedColumnStyle(getWidth(columnKey))
    },
    [allowColumnResize, getWidth]
  )

  const filterPills = useMemo(
    () => generateFilterPills(filterTree, filterFields),
    [filterTree, filterFields]
  )

  const activeFilterCount = useMemo(
    () => countFilterConditions(filterTree),
    [filterTree]
  )

  const hasActiveTextSearch = textSearch.trim().length > 0
  const showingSubset =
    activeFilterCount > 0 ||
    hasActiveTextSearch ||
    sortedRules.length !== rules.length

  const handleRemoveFilterPill = useCallback(
    (pillId: string) => {
      setFilterTree(removeFilterPill(filterTree, pillId))
    },
    [filterTree, setFilterTree]
  )

  const handleClearAllFilters = useCallback(() => {
    clearFilters()
  }, [clearFilters])

  function renderHeaderCell(columnKey: EmailRulesColumnKey) {
    if (columnKey === 'actions') {
      return (
        <th key={columnKey} style={headerStyle(columnKey)}>
          <span className="inline-flex items-center pr-2">{EMAIL_RULES_COLUMN_LABELS.actions}</span>
          {allowColumnResize ? (
            <ColumnResizeHandle columnKey="actions" onStartResize={startResize} />
          ) : null}
        </th>
      )
    }

    const sortKey = columnKey === 'pdf_hints' ? 'pdf_hints' : columnKey
    const responsiveClass =
      columnKey === 'from_filter'
        ? 'hidden sm:table-cell'
        : columnKey === 'subject_contains' || columnKey === 'pdf_hints'
          ? 'hidden md:table-cell'
          : columnKey === 'email_has_words' || columnKey === 'notes'
            ? 'hidden lg:table-cell'
            : ''

    return (
      <EmailRulesSortableHeader
        key={columnKey}
        label={EMAIL_RULES_COLUMN_LABELS[columnKey]}
        sortKey={sortKey}
        sortDirection={isEmailRulesColumnSortable(columnKey) ? getSortDirection(sortKey) : null}
        onSort={handleRequestSort}
        className={responsiveClass}
        columnKey={columnKey}
        style={headerStyle(columnKey)}
        onStartResize={allowColumnResize ? startResize : undefined}
        showResize={allowColumnResize}
      />
    )
  }

  function renderCell(columnKey: EmailRulesColumnKey, rule: EmailRule) {
    const responsiveClass =
      columnKey === 'from_filter'
        ? 'hidden sm:table-cell'
        : columnKey === 'subject_contains' || columnKey === 'pdf_hints'
          ? 'hidden md:table-cell'
          : columnKey === 'email_has_words' || columnKey === 'notes'
            ? 'hidden lg:table-cell'
            : ''

    switch (columnKey) {
      case 'vendor_name':
        return (
          <td
            key={columnKey}
            className={`font-medium overflow-hidden ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={
              rule.is_negative_rule ? `${rule.vendor_name} (Exclude)` : rule.vendor_name
            }
          >
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="min-w-0 truncate">{rule.vendor_name}</span>
              {rule.is_negative_rule ? (
                <span
                  className="badge badge-danger text-[10px] shrink-0"
                  title="Negative rule: excluded from automated processing"
                >
                  Exclude
                </span>
              ) : null}
            </div>
          </td>
        )
      case 'from_filter':
        return (
          <td
            key={columnKey}
            className={`text-gray-600 truncate ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={rule.from_filter || ''}
          >
            {rule.from_filter || <span className="text-gray-300">—</span>}
          </td>
        )
      case 'subject_contains':
        return (
          <td
            key={columnKey}
            className={`text-gray-600 truncate ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={rule.subject_contains || ''}
          >
            {rule.subject_contains || <span className="text-gray-300">—</span>}
          </td>
        )
      case 'email_has_words':
        return (
          <td
            key={columnKey}
            className={`text-gray-600 truncate ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={rule.email_has_words || ''}
          >
            {rule.email_has_words || <span className="text-gray-300">—</span>}
          </td>
        )
      case 'destinations':
        return (
          <td key={columnKey} className={responsiveClass} style={cellStyle(columnKey)}>
            {rule.is_negative_rule ? (
              <span className="text-xs text-gray-400">—</span>
            ) : (
              <div className="flex gap-1 flex-wrap">
                {rule.destinations.map((dest) => (
                  <span key={dest} className="badge badge-info text-xs">
                    {dest}
                  </span>
                ))}
              </div>
            )}
          </td>
        )
      case 'status':
        return (
          <td key={columnKey} style={cellStyle(columnKey)}>
            <button
              type="button"
              onClick={() => toggleStatus(rule)}
              className={`badge cursor-pointer transition-colors ${
                rule.status === 'confirmed'
                  ? 'badge-success hover:bg-green-200'
                  : rule.status === 'disabled'
                    ? 'badge-neutral hover:bg-gray-200'
                    : 'badge-warning hover:bg-amber-200'
              }`}
              title="Click to toggle status"
            >
              {rule.status}
            </button>
          </td>
        )
      case 'pdf_hints':
        return (
          <td
            key={columnKey}
            className={`text-gray-600 text-center ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={
              rule.extraction_directive?.trim()
                ? 'Custom PDF extraction hints are set'
                : undefined
            }
          >
            {rule.extraction_directive?.trim() ? (
              <span className="text-emerald-700 text-xs font-medium">Yes</span>
            ) : (
              <span className="text-gray-300">—</span>
            )}
          </td>
        )
      case 'notes':
        return (
          <td
            key={columnKey}
            className={`text-gray-600 truncate ${responsiveClass}`}
            style={cellStyle(columnKey)}
            title={rule.notes || ''}
          >
            {rule.notes || <span className="text-gray-300">—</span>}
          </td>
        )
      case 'actions':
        return (
          <td key={columnKey} style={cellStyle(columnKey)}>
            <div className="flex gap-1 md:opacity-50 md:group-hover:opacity-100 transition-opacity">
              <button
                type="button"
                onClick={() => openEmailSearchForRule(rule.id)}
                className="btn btn-ghost p-1"
                title="Search emails with this rule"
              >
                <MagnifyingGlassIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => copyGmailQuery(rule)}
                className="btn btn-ghost p-1 hidden sm:inline-flex"
                title="Copy Gmail query"
              >
                <ClipboardDocumentIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => setEditingRule(rule)}
                className="btn btn-ghost p-1"
                title="Edit"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                type="button"
                onClick={() => handleDelete(rule.id)}
                className="btn btn-ghost p-1 text-red-600 hover:text-red-700"
                title="Delete"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </td>
        )
      default:
        return null
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading email rules...</div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {isCreating ? (
        <div className="flex-shrink-0 mb-4">
          <EmailRuleForm onSubmit={handleCreate} onCancel={() => setIsCreating(false)} />
        </div>
      ) : null}

      <TablePageShell
        className="min-h-0 flex-1"
        rowCount={
          <div className="mb-2 text-xs text-gray-500 sm:text-sm">
            <span className="font-medium text-gray-700">
              {showingSubset
                ? `${sortedRules.length} rule${sortedRules.length !== 1 ? 's' : ''} shown`
                : `${rules.length} rule${rules.length !== 1 ? 's' : ''} configured`}
            </span>
            {showingSubset ? (
              <span className="ml-1">(of {rules.length.toLocaleString()} total)</span>
            ) : null}
          </div>
        }
        toolbarLeft={
          <SearchInput
            value={textSearch}
            onChange={setTextSearch}
            placeholder="Search vendor, from, subject…"
            className="max-w-md flex-1"
          />
        }
        toolbarRight={
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setIsCreating(true)}
              className="btn btn-primary"
            >
              <PlusIcon className="w-4 h-4" />
              <span className="hidden sm:inline">Add Rule</span>
            </button>
            <TableToolbar
              filterCount={activeFilterCount}
              sortCount={sortSpecs.length}
              showFilters={showFilters}
              showSort={showSort}
              showColumns={showColumns}
              onToggleFilters={() => setShowFilters((open) => !open)}
              onToggleSort={() => setShowSort((open) => !open)}
              onToggleColumns={() => setShowColumns((open) => !open)}
            />
          </div>
        }
        filterPills={
          <ActiveFilterPillBar
            pills={filterPills}
            onRemovePill={handleRemoveFilterPill}
            onClearAll={handleClearAllFilters}
          />
        }
      >
        <div className="table-scroll-mobile min-h-0 overflow-x-auto overflow-y-auto overscroll-x-contain">
          <table
            className="table-forecast border-separate border-spacing-0"
            style={tableStyle}
          >
            <thead>
              <tr>
                {visibleColumnKeys.map((columnKey) =>
                  renderHeaderCell(columnKey as EmailRulesColumnKey)
                )}
              </tr>
            </thead>
            <tbody>
              {sortedRules.length === 0 ? (
                <tr>
                  <td
                    colSpan={visibleColumnCount}
                    className="py-12 text-center text-sm text-gray-600 px-4"
                  >
                    {rules.length === 0 ? (
                      'No email rules configured. Click "Add Rule" to create one.'
                    ) : (
                      <>
                        No rules match your filters.{' '}
                        <button
                          type="button"
                          className="font-medium text-blue-600 hover:underline"
                          onClick={handleClearAllFilters}
                        >
                          Clear filters
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ) : (
                sortedRules.map((rule) => (
                  <tr key={rule.id} className="group">
                    {visibleColumnKeys.map((columnKey) =>
                      renderCell(columnKey as EmailRulesColumnKey, rule)
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </TablePageShell>

      <FilterBuilderDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        fields={filterFields}
        root={filterTree}
        onChange={setFilterTree}
        onClearAll={() => setFilterTree(null)}
      />

      <SortBuilder
        open={showSort}
        fields={EMAIL_RULES_FIELD_DEFINITIONS}
        sorts={sortSpecs}
        onChange={setSortSpecs}
        onClose={() => setShowSort(false)}
        onClearAll={() => setSortSpecs(EMAIL_RULES_DEFAULT_SORT)}
      />

      <ColumnPicker
        open={showColumns}
        fields={EMAIL_RULES_FIELD_DEFINITIONS}
        visibility={columnVisibility}
        onChange={handleColumnVisibilityChange}
        onClose={() => setShowColumns(false)}
        onReset={() => {
          handleColumnVisibilityChange(getDefaultColumnVisibility(EMAIL_RULES_FIELD_DEFINITIONS))
        }}
      />

      {editingRule ? (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="max-h-[95vh] w-full min-w-0 max-w-lg overflow-y-auto rounded-xl bg-white shadow-2xl sm:max-h-[90vh]">
            <div className="sticky top-0 flex items-center justify-between border-b border-gray-100 bg-white px-4 py-3 sm:px-6 sm:py-4">
              <h3 className="min-w-0 truncate pr-2 text-lg font-semibold text-gray-900">
                Edit Rule
              </h3>
              <button
                type="button"
                onClick={() => setEditingRule(null)}
                className="btn btn-ghost p-1"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-6">
              <EmailRuleForm
                initialData={editingRule}
                onSubmit={(data) => handleUpdate(editingRule.id, data)}
                onCancel={() => setEditingRule(null)}
                isModal
              />
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
